import type { Language } from "client";
import moment from "moment-timezone";

export type SessionCsvTemplate = {
  columns: Array<{
    name: string;
    key: string;
    required?: boolean;
    description?: string;
    data_type?: string;
    suggested_mappings?: string[];
  }>;
};

function withDisplayNameMappings<
  T extends {
    name: string;
    key: string;
    suggested_mappings?: string[];
  },
>(column: T): T {
  const mappings: string[] = [];
  const seen = new Set<string>();

  const addMapping = (value: string) => {
    const normalized = value.trim();
    if (!normalized) return;

    const key = normalized.toLowerCase();
    if (seen.has(key)) return;

    seen.add(key);
    mappings.push(normalized);
  };

  for (const mapping of column.suggested_mappings ?? []) {
    addMapping(mapping);
  }

  addMapping(column.name);
  addMapping(column.key);

  return {
    ...column,
    suggested_mappings: mappings,
  };
}

export function buildSessionCsvTemplate(
  languages: Pick<Language, "code" | "name">[]
): SessionCsvTemplate {
  const columns: SessionCsvTemplate["columns"] = [
    {
      name: "Date",
      key: "date",
      required: true,
      description: "Session date (YYYY-MM-DD)",
      suggested_mappings: ["date", "session_date"],
    },
    {
      name: "Start Time",
      key: "startTime",
      required: true,
      description: "Start time in 24h format (HH:mm)",
      suggested_mappings: ["start_time", "start", "startTime"],
    },
    {
      name: "End Time",
      key: "endTime",
      required: true,
      description: "End time in 24h format (HH:mm)",
      suggested_mappings: ["end_time", "end", "endTime"],
    },
    {
      name: "Topics",
      key: "topics",
      required: false,
      description:
        "Comma-separated topics for the default language (legacy). Prefer Topics (EN) / Topics (AR) columns when present.",
      suggested_mappings: ["topics", "session_topics"],
    },
    {
      name: "Speaker Emails",
      key: "speakerEmails",
      required: true,
      description: "Comma-separated speaker emails",
      suggested_mappings: ["speaker_emails", "speakers", "speakerEmails"],
    },
    {
      name: "Speaker Names",
      key: "speakerNames",
      required: false,
      description:
        "Comma-separated speaker names matching email order (used when creating new speakers)",
      suggested_mappings: ["speaker_names", "speakerNames"],
    },
    {
      name: "Attendee Limit",
      key: "attendeeLimit",
      required: false,
      data_type: "number",
      description: "Maximum attendees for the session",
      suggested_mappings: ["attendee_limit", "limit", "attendeeLimit"],
    },
  ];

  for (const language of languages) {
    const code = language.code;
    const label = code.toUpperCase();

    columns.push(
      {
        name: `Name (${label})`,
        key: `name_${code}`,
        required: code === "en",
        description: `Session name in ${language.name}`,
        suggested_mappings: [`name_${code}`, `session_name_${code}`],
      },
      {
        name: `Description (${label})`,
        key: `description_${code}`,
        description: `Session description in ${language.name}`,
        suggested_mappings: [`description_${code}`],
      },
      {
        name: `Location (${label})`,
        key: `location_${code}`,
        description: `Session location in ${language.name}`,
        suggested_mappings: [`location_${code}`],
      },
      {
        name: `Track (${label})`,
        key: `track_${code}`,
        description: `Session track in ${language.name}`,
        suggested_mappings: [`track_${code}`],
      },
      {
        name: `Type (${label})`,
        key: `type_${code}`,
        description: `Session type in ${language.name}`,
        suggested_mappings: [`type_${code}`, `session_type_${code}`],
      },
      {
        name: `Topics (${label})`,
        key: `topics_${code}`,
        description: `Comma-separated session topics in ${language.name}`,
        suggested_mappings: [`topics_${code}`, `session_topics_${code}`],
      }
    );
  }

  return { columns: columns.map(withDisplayNameMappings) };
}

function escapeCsvField(value: string): string {
  const normalized = String(value);
  if (/[",\n\r]/.test(normalized) || normalized.includes(" ")) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
}

export function buildSessionCsvSampleContent(
  template: SessionCsvTemplate
): string {
  const headers = template.columns.map((column) => column.name);
  const sampleRow = template.columns.map((column) => {
    if (column.key === "date") return "2025-06-16";
    if (column.key === "startTime") return "10:00";
    if (column.key === "endTime") return "11:00";
    if (column.key === "topics") return "Innovation,Technology";
    if (column.key.startsWith("topics_")) {
      return column.key.endsWith("_ar") ? "ابتكار,تكنولوجيا" : "Innovation,Technology";
    }
    if (column.key === "speakerEmails") return "speaker@example.com";
    if (column.key === "speakerNames") return "John Doe";
    if (column.key === "attendeeLimit") return "500";
    if (column.key.startsWith("name_")) return "Sample Session";
    if (column.key.startsWith("description_")) return "Sample description";
    if (column.key.startsWith("location_")) return "Main Hall";
    if (column.key.startsWith("track_")) return "Main Track";
    if (column.key.startsWith("type_")) return "Workshop";
    return "";
  });

  return [
    headers.map(escapeCsvField).join(","),
    sampleRow.map(escapeCsvField).join(","),
  ].join("\n");
}

type ExportSession = {
  startDate: Date | string;
  endDate: Date | string;
  topics?: string[];
  limit?: number | null;
  speakers?: Array<{ email?: string; name?: string }>;
  translations?: Array<{
    name?: string | null;
    description?: string | null;
    location?: string | null;
    track?: string | null;
    type?: string | null;
    topics?: string[] | null;
    language?: { code?: string };
  }>;
};

export function buildSessionCsvExportContent(
  sessions: ExportSession[],
  template: SessionCsvTemplate,
  timezone: string
): string {
  const headers = template.columns.map((column) => column.name);

  const rows = sessions.map((session) => {
    const start = moment(session.startDate).tz(timezone);
    const end = moment(session.endDate).tz(timezone);
    const translationByCode = new Map(
      (session.translations ?? []).map((translation) => [
        translation.language?.code ?? "",
        translation,
      ])
    );

    return template.columns.map((column) => {
      if (column.key === "date") return start.format("YYYY-MM-DD");
      if (column.key === "startTime") return start.format("HH:mm");
      if (column.key === "endTime") return end.format("HH:mm");
      if (column.key === "topics") {
        const defaultTranslation =
          translationByCode.get("en") ?? session.translations?.[0];
        const defaultTopics = defaultTranslation?.topics?.length
          ? defaultTranslation.topics
          : session.topics;
        return (defaultTopics ?? []).join(",");
      }
      if (column.key === "speakerEmails") {
        return (session.speakers ?? [])
          .map((speaker) => speaker.email ?? "")
          .filter(Boolean)
          .join(",");
      }
      if (column.key === "speakerNames") {
        return (session.speakers ?? [])
          .map((speaker) => speaker.name ?? "")
          .filter(Boolean)
          .join(",");
      }
      if (column.key === "attendeeLimit") {
        return session.limit != null ? String(session.limit) : "";
      }

      const langMatch = column.key.match(
        /^(name|description|location|track|type|topics)_(.+)$/
      );
      if (langMatch) {
        const [, field, code] = langMatch;
        const translation = translationByCode.get(code);
        if (field === "topics") {
          const value = translation?.topics;
          return Array.isArray(value) ? value.join(",") : "";
        }
        const value = translation?.[field as keyof typeof translation];
        return value != null ? String(value) : "";
      }

      return "";
    });
  });

  return [
    headers.map(escapeCsvField).join(","),
    ...rows.map((row) => row.map(escapeCsvField).join(",")),
  ].join("\n");
}
